/**
 * Shared domain helpers (F1): local-first reads + action
 * gating with the SAME compiled permissions that govern sync.
 *
 * Reads: the local SQLite already contains ONLY what is authorized (buckets by
 * `effective_grant`), so pages render what was synced without
 * re-filtering. Writes: Drizzle over PowerSync (local-first CRUD queue,
 * works OFFLINE and reconciles on reconnect); the server re-validates each
 * operation with `require_grant`.
 */

import { type Accessor, createMemo } from "solid-js";
import {
  canAt,
  type EffectivePermission,
  getEffectiveGrantsQuery,
  type GrantAction,
  queryKeys,
  type ScopeNodeKind,
} from "@agape/shared";
import { createPowerSyncQuery } from "@agape/shared/solid";
import type { EffectiveGrantRecord, PowerSyncDrizzleDB } from "@agape/powersync";

import { generateId } from "@fleet/utils";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";

export function newId(): string {
  return generateId();
}

export function nowIso(): string {
  return new Date().toISOString();
}

function isNodeKind(value: string | null): value is ScopeNodeKind {
  return value === "org" || value === "church" || value === "venue" || value === "group";
}

function toNodeKind(value: string | null): ScopeNodeKind {
  return isNodeKind(value) ? value : "church";
}

/** Typed local-first query with automatic invalidation by PowerSync tables. */
export function useDomainQuery<T extends object>(
  key: (userId: string | undefined) => readonly unknown[],
  fn: (
    db: PowerSyncDrizzleDB,
  ) => { execute(): Promise<T | T[]>; toSQL(): { sql: string; params: unknown[] } } | null,
) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return createPowerSyncQuery<T>({
    queryKey: () => key(userId()),
    db: () => db(),
    queryFn: () => {
      const instance = drizzle();
      return instance ? fn(instance) : null;
    },
    enabled: () => Boolean(drizzle()),
  });
}

interface DomainGrants {
  effective: Accessor<EffectivePermission[]>;
  /** Grants the requested level at ANY candidate node? (group→local→church→org). */
  can: (
    nodeIds: readonly (string | null | undefined)[],
    capability: string,
    level: GrantAction,
  ) => boolean;
  /** Nodes of one kind where the account has the requested capability level. */
  nodesWith: (capability: string, level: GrantAction, kind?: ScopeNodeKind) => string[];
}

/** Effective permissions for the active account (same source as sync). */
export function useGrants(): DomainGrants {
  const { userId } = useSession();
  const query = useDomainQuery<EffectiveGrantRecord>(
    (uid) => queryKeys.domain.effectiveGrants(uid),
    (db) => {
      const uid = userId();
      return uid ? getEffectiveGrantsQuery(db, uid) : null;
    },
  );
  const effective = createMemo<EffectivePermission[]>(() =>
    (query.data ?? []).map((row) => ({
      capabilityId: row.capability_id ?? "",
      nodeId: row.node_id ?? "",
      nodeKind: toNodeKind(row.node_kind),
      churchId: row.church_id,
      canRead: Boolean(row.can_read),
      canWrite: Boolean(row.can_write),
      canManage: Boolean(row.can_manage),
    })),
  );
  const can: DomainGrants["can"] = (nodeIds, capability, level) =>
    nodeIds
      .filter((node): node is string => Boolean(node))
      .some((node) => canAt(effective(), node, capability, level));
  const nodesWith: DomainGrants["nodesWith"] = (capability, level, kind) =>
    effective()
      .filter(
        (p) =>
          p.capabilityId === capability &&
          (kind === undefined || p.nodeKind === kind) &&
          canAt([p], p.nodeId, capability, level),
      )
      .map((p) => p.nodeId);
  return { effective, can, nodesWith };
}

/** Imperative Drizzle instance for mutations (throws until the local DB is ready). */
export function useWriter(): () => PowerSyncDrizzleDB {
  const drizzle = useDrizzle();
  return () => {
    const instance = drizzle();
    if (!instance) {
      throw new Error("Local database is not ready yet");
    }
    return instance;
  };
}

/** Human-readable timestamp label (date + short time). */
export function formatWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** `datetime-local` ⇄ ISO UTC. */
export function localInputToIso(value: string): string | null {
  if (!value) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}
